import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import { ArrowLeft, Edit, Users, MapPin, Calendar, DollarSign, Clock, Mail, ExternalLink } from 'lucide-react';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { RouteOffer } from '@/types/routeOffers';
import { formatCurrency, formatDate } from '@/lib/utils';

export default function RouteOfferDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { toast } = useToast();

  const [offer, setOffer] = useState<RouteOffer | null>(null);
  const [loading, setLoading] = useState(true);

  // Redirect if not admin
  useEffect(() => {
    if (role && role !== 'admin') {
      navigate('/dashboard');
      toast({
        title: 'Access Denied',
        description: 'You need admin privileges to access this page.',
        variant: 'destructive',
      });
    }
  }, [role, navigate, toast]);

  // Load offer
  useEffect(() => {
    console.log('[RouteOfferDetail] useEffect triggered. id:', id, 'user:', !!user, 'role:', role);

    if (!id || !user || role !== 'admin') {
      console.log('[RouteOfferDetail] Skipping load - missing requirements');
      return;
    }

    const loadOffer = async () => {
      try {
        console.log('[RouteOfferDetail] Starting to load offer:', id);
        setLoading(true);
        const offerRef = doc(db, 'routeOffers', id);
        const offerDoc = await getDoc(offerRef);

        console.log('[RouteOfferDetail] Offer doc exists:', offerDoc.exists());

        if (!offerDoc.exists()) {
          console.error('[RouteOfferDetail] Offer not found:', id);
          toast({
            title: 'Not Found',
            description: 'Route offer not found.',
            variant: 'destructive',
          });
          navigate('/admin/route-offers');
          return;
        }

        const data = offerDoc.data();
        const offerData: RouteOffer = {
          ...data,
          id: offerDoc.id,
          createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
          updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
          expiresAt: (data.expiresAt as Timestamp)?.toDate() || new Date(),
          sentAt: (data.sentAt as Timestamp)?.toDate(),
          filledAt: (data.filledAt as Timestamp)?.toDate(),
          cancelledAt: (data.cancelledAt as Timestamp)?.toDate(),
          recipients: data.recipients?.map((r: any) => ({
            ...r,
            viewedAt: (r.viewedAt as Timestamp)?.toDate(),
            respondedAt: (r.respondedAt as Timestamp)?.toDate(),
            paidAt: (r.paidAt as Timestamp)?.toDate(),
            notifiedAt: (r.notifiedAt as Timestamp)?.toDate(),
          })) || [],
        } as RouteOffer;

        setOffer(offerData);
        console.log('[RouteOfferDetail] Offer loaded successfully:', offerData.routeName);
      } catch (error) {
        console.error('[RouteOfferDetail] Error loading offer:', error);
        toast({
          title: 'Error',
          description: 'Failed to load route offer. Please try again.',
          variant: 'destructive',
        });
      } finally {
        console.log('[RouteOfferDetail] Setting loading to false');
        setLoading(false);
      }
    };

    loadOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, role]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'partially_filled':
        return 'bg-blue-100 text-blue-800';
      case 'filled':
        return 'bg-purple-100 text-purple-800';
      case 'expired':
        return 'bg-orange-100 text-orange-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRecipientStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'viewed':
        return 'bg-blue-100 text-blue-800';
      case 'accepted':
        return 'bg-yellow-100 text-yellow-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'enrolled':
        return 'bg-purple-100 text-purple-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Loading offer details...</p>
        </div>
      </div>
    );
  }

  if (!offer) {
    return null;
  }

  const tier1Recipients = offer.recipients.filter((r) => r.tier === 1);
  const tier2Recipients = offer.recipients.filter((r) => r.tier === 2);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/route-offers')}
          className="mb-6 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Route Offers
        </Button>

        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{offer.routeName}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(offer.status)}`}>
                {getStatusLabel(offer.status)}
              </span>
            </div>
            <p className="text-gray-600">
              Created {formatDate(offer.createdAt)}
              {offer.sentAt && ` • Sent ${formatDate(offer.sentAt)}`}
            </p>
            <p className="text-xs text-gray-500 font-mono mt-1">
              Offer ID: {offer.id}
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                const editPath = `/admin/route-offers/${offer.id}/edit`;
                console.log('[RouteOfferDetail] Send offer button clicked. Navigating to:', editPath);
                console.log('[RouteOfferDetail] Offer ID:', offer.id);
                navigate(editPath);
              }}
              className="flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Send Offer
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Route Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Route Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Pickup Location</label>
                    <p className="text-gray-900">{offer.pickupLocation}</p>
                    {offer.pickupTime && (
                      <p className="text-sm text-gray-600">{offer.pickupTime}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Dropoff Location</label>
                    <p className="text-gray-900">{offer.dropoffLocation}</p>
                    {offer.dropoffTime && (
                      <p className="text-sm text-gray-600">{offer.dropoffTime}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Capacity & Availability */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Capacity & Availability
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{offer.maxStudents}</div>
                    <div className="text-sm text-gray-600">Max Students</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{offer.currentEnrollment}</div>
                    <div className="text-sm text-gray-600">Enrolled</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {offer.maxStudents - offer.currentEnrollment}
                    </div>
                    <div className="text-sm text-gray-600">Available</div>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Available Weeks</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {offer.availableWeeks.sort((a, b) => a - b).map((week) => (
                      <span key={week} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                        Week {week}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Pricing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Base Weekly Rate</span>
                  <span className="font-medium">{formatCurrency(offer.baseWeeklyRate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Adjustment</span>
                  <span className={offer.percentageAdjustment >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {offer.percentageAdjustment >= 0 ? '+' : ''}{offer.percentageAdjustment}%
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t text-lg">
                  <span className="font-semibold">Adjusted Rate (per student/week)</span>
                  <span className="font-bold text-primary">{formatCurrency(offer.adjustedWeeklyRate)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Recipients */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Recipients ({offer.recipients.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tier1Recipients.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Priority Recipients</h3>
                    <div className="space-y-3">
                      {tier1Recipients.map((recipient, index) => (
                        <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="font-medium text-gray-900">{recipient.name}</div>
                              <div className="text-sm text-gray-600">{recipient.email}</div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRecipientStatusColor(recipient.status)}`}>
                              {getStatusLabel(recipient.status)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mt-2">
                            <div>
                              <span className="font-medium">Students:</span> {recipient.studentCount}
                            </div>
                            <div>
                              <span className="font-medium">Rate:</span> {formatCurrency(recipient.weeklyRate)}/week
                            </div>
                            {recipient.selectedWeeks && recipient.selectedWeeks.length > 0 && (
                              <>
                                <div>
                                  <span className="font-medium">Weeks:</span> {recipient.selectedWeeks.length}
                                </div>
                                <div>
                                  <span className="font-medium">Total:</span> {formatCurrency(recipient.totalAmount)}
                                </div>
                              </>
                            )}
                          </div>
                          {recipient.quoteId && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <Link
                                to={`/admin/quote-requests/${recipient.quoteId}`}
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" />
                                View Original Quote
                              </Link>
                            </div>
                          )}
                          {recipient.enrollmentId && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                ✓ Enrollment ID: {recipient.enrollmentId}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {tier2Recipients.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Waitlist</h3>
                    <div className="space-y-3">
                      {tier2Recipients.map((recipient, index) => (
                        <div key={index} className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="font-medium text-gray-900">{recipient.name}</div>
                              <div className="text-sm text-gray-600">{recipient.email}</div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRecipientStatusColor(recipient.status)}`}>
                              Waitlist - {getStatusLabel(recipient.status)}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Students:</span> {recipient.studentCount}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-gray-400 mt-2" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Created</div>
                      <div className="text-sm text-gray-500">{formatDate(offer.createdAt)}</div>
                    </div>
                  </div>
                  {offer.sentAt && (
                    <div className="flex gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">Sent</div>
                        <div className="text-sm text-gray-500">{formatDate(offer.sentAt)}</div>
                      </div>
                    </div>
                  )}
                  {offer.expiresAt && (
                    <div className="flex gap-3">
                      <div className="w-2 h-2 rounded-full bg-orange-500 mt-2" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {new Date(offer.expiresAt) > new Date() ? 'Expires' : 'Expired'}
                        </div>
                        <div className="text-sm text-gray-500">{formatDate(offer.expiresAt)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Source Quote */}
            {offer.sourceQuoteId && (
              <Card>
                <CardHeader>
                  <CardTitle>Source Quote Request</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">
                    This offer was automatically created from a customer quote request.
                  </p>
                  <Link to={`/admin/quote-requests/${offer.sourceQuoteId}`}>
                    <Button variant="outline" className="w-full flex items-center gap-2">
                      <ExternalLink className="w-4 h-4" />
                      View Original Quote Request
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}

            {/* Admin Notes */}
            {offer.adminNotes && (
              <Card>
                <CardHeader>
                  <CardTitle>Admin Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{offer.adminNotes}</p>
                </CardContent>
              </Card>
            )}

            {/* Customer Message */}
            {offer.customerMessage && (
              <Card>
                <CardHeader>
                  <CardTitle>Customer Message</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{offer.customerMessage}</p>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(`/admin/route-offers/create?offerId=${offer.id}`)}
                >
                  Add More Recipients
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    const emails = offer.recipients.map(r => r.email).join(', ');
                    window.open(`mailto:${emails}`, '_blank');
                  }}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Email All Recipients
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
